/**
 * 'Bench 에서 고치기' 오가기(SR-INT-02 · CRQ-004 D-4 후속 · 2026-09-28 결정).
 *
 * 원칙: 세션은 탭 메모리(sessionStorage)에만 둔다. Bench 로 갈 때는 제어 평면이 Bench 에서 받아 준 1회용 티켓이 든 editorUrl 을
 * 새 탭으로 열고(POST /api/courses/{c}/bench/handoff), 돌아올 때는 Bench 탭이 `window.opener.postMessage` 로 Field 탭을 깨운다 —
 * Field 탭은 살아 있다.
 *
 *  - 받는 메시지: `{type:"bench.fieldReturn", v:1, benchVersionHash:"sha256:<64hex>", projectId?}` 를 **설정된 Bench 웹 출처에서만**
 *    (event.origin 정확히 같음). 연 탭을 알면 event.source 도 그 탭이어야 한다. 그 밖은 모두 버린다.
 *  - Field 탭이 없어졌을 때(폴백): Bench 는 `<returnUrl>?benchVersionHash=sha256:…` 로 간다. 셸은 부팅 때 그 값을 읽어 주소에서 지우고
 *    `{projectId?, benchVersionHash}` 를 localStorage 에 30분 둔다(PII 없음). 세션이 없으면 '캠퍼스에서 다시 열어 주세요' —
 *    문 B 로 다시 들어오면 룸 화면이 그 저장본을 가져오고 지운다.
 */

export const BENCH_RETURN_TYPE = "bench.fieldReturn";
export const BENCH_RETURN_V = 1;
export const PENDING_KEY = "pf.benchReturn.v1";
export const PENDING_TTL_MS = 30 * 60 * 1000;

const HASH = /^sha256:[0-9a-f]{64}$/;
const PROJECT = /^[A-Za-z0-9_.:-]{1,100}$/;

/** Bench 에서 돌아온 저장본 — projectId 는 알면(메시지 · 쿼리 · 연 탭의 기억) */
export interface BenchReturn {
  benchVersionHash: string;
  projectId: string | null;
}

const projectOrNull = (v: unknown): string | null =>
  typeof v === "string" && PROJECT.test(v) ? v : null;

/**
 * postMessage 거르기 — 출처 · 종류 · 판이 맞고 해시 형식이 맞을 때만 값. expectedSource 가 있으면 보낸 창도 같아야 한다.
 */
export function acceptBenchReturn(
  ev: Pick<MessageEvent, "origin" | "data" | "source">,
  benchOrigin: string | null,
  expectedSource?: unknown,
): BenchReturn | null {
  if (!benchOrigin || ev.origin !== benchOrigin) return null;
  if (expectedSource != null && ev.source !== expectedSource) return null;
  const d = ev.data as Record<string, unknown> | null;
  if (!d || typeof d !== "object" || d.type !== BENCH_RETURN_TYPE || d.v !== BENCH_RETURN_V)
    return null;
  const hash = typeof d.benchVersionHash === "string" ? d.benchVersionHash : d.versionHash;
  if (typeof hash !== "string" || !HASH.test(hash)) return null;
  return { benchVersionHash: hash, projectId: projectOrNull(d.projectId) };
}

/** editorUrl 이 설정된 Bench 출처의 http(s) 주소인가(서버가 준 것이라도 다른 곳은 열지 않는다) */
export function isBenchUrl(url: unknown, benchOrigin: string | null): url is string {
  if (typeof url !== "string" || !benchOrigin) return false;
  try {
    const u = new URL(url);
    return (u.protocol === "https:" || u.protocol === "http:") && u.origin === benchOrigin;
  } catch {
    return false;
  }
}

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function safeLocal(): Store | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

/** 돌아온 저장본을 30분 맡긴다(같은 탭 · 다른 탭 · 다시 들어온 뒤 — 세션과 무관 · PII 없음) */
export function savePendingReturn(
  r: BenchReturn,
  store: Store | null = safeLocal(),
  now = Date.now(),
): void {
  try {
    store?.setItem(
      PENDING_KEY,
      JSON.stringify({
        benchVersionHash: r.benchVersionHash,
        projectId: r.projectId,
        savedAt: now,
      }),
    );
  } catch {
    // 저장소를 쓸 수 없으면(사생활 보호 모드) 맡기지 않는다 — 학생은 '새 코드 가져오기'로 최신을 가져올 수 있다
  }
}

/** 맡긴 저장본(지우지 않음) — 없거나 30분 지났거나 형식 밖이면 null(지난 것은 지운다) */
export function peekPendingReturn(
  store: Store | null = safeLocal(),
  now = Date.now(),
): BenchReturn | null {
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(PENDING_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as {
      benchVersionHash?: unknown;
      projectId?: unknown;
      savedAt?: unknown;
    };
    const fresh =
      typeof v.savedAt === "number" && now - v.savedAt >= 0 && now - v.savedAt <= PENDING_TTL_MS;
    if (fresh && typeof v.benchVersionHash === "string" && HASH.test(v.benchVersionHash))
      return { benchVersionHash: v.benchVersionHash, projectId: projectOrNull(v.projectId) };
  } catch {
    // 형식 밖 — 아래에서 지운다
  }
  clearPendingReturn(store);
  return null;
}

export function clearPendingReturn(store: Store | null = safeLocal()): void {
  try {
    store?.removeItem(PENDING_KEY);
  } catch {
    // 무시
  }
}

/** 맡긴 저장본을 꺼내고 지운다 */
export function takePendingReturn(
  store: Store | null = safeLocal(),
  now = Date.now(),
): BenchReturn | null {
  const r = peekPendingReturn(store, now);
  if (r) clearPendingReturn(store);
  return r;
}

/**
 * 폴백 — 부팅 때 `?benchVersionHash=sha256:…[&projectId=…]` 를 읽어 맡기고 주소에서 지운다(히스토리에 남기지 않음).
 * 형식 밖이면 매개변수만 지우고 맡기지 않는다.
 */
export function captureBenchReturnFromUrl(
  loc: Pick<Location, "search" | "pathname" | "hash"> = window.location,
  replace: (url: string) => void = (url) =>
    window.history.replaceState(window.history.state, "", url),
  store: Store | null = safeLocal(),
  now = Date.now(),
): BenchReturn | null {
  const q = new URLSearchParams(loc.search);
  if (!q.has("benchVersionHash")) return null;
  const hash = q.get("benchVersionHash") ?? "";
  const projectId = projectOrNull(q.get("projectId") ?? q.get("benchProjectId"));
  for (const k of ["benchVersionHash", "projectId", "benchProjectId"]) q.delete(k);
  const rest = q.toString();
  replace(`${loc.pathname}${rest ? `?${rest}` : ""}${loc.hash}`);
  if (!HASH.test(hash)) return null;
  const r = { benchVersionHash: hash, projectId };
  savePendingReturn(r, store, now);
  return r;
}

/** Bench 가 돌아올 주소 — 지금 화면(쿼리 · 프래그먼트 없이 · 제어 평면이 셸 출처만 받는다) */
export function returnUrlHere(
  loc: Pick<Location, "origin" | "pathname"> = window.location,
): string {
  return `${loc.origin}${loc.pathname}`;
}
